import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { FeeItemType, PermitFeeItem } from '../../modules/clientModule/types';
import { InvoiceHeader, InvoicePage, ProjectPermitInvoiceType } from '../../Components/InvoiceTool/types';
import { INVOICES_TO_PAY_KEY, SELECTED_PERMIT_ID } from '../../settings';

export interface FeesState {
  inspectionInvoicesList: PermitFeeItem[];
  selectedProjectInvoice: ProjectPermitInvoiceType;
  selectedFee: PermitFeeItem;
  permitFees: FeeItemType[];
  invoiceHeader: InvoiceHeader;
  invoiceBody: InvoicePage[];
  invoicePdf: string;
  // EA-1391: the invoice number the current `invoicePdf` bytes were generated
  // for. The emailed PDF is 100% client-supplied (the backend writes these
  // bytes to disk verbatim and attaches them), so Send must only transmit a
  // PDF that provably belongs to the invoice being sent. Empty string means
  // "no PDF is bound to any invoice right now".
  invoicePdfInvoiceNumber: string;
  invoiceNumber: string;
  hasViewClicked: boolean;
  isGeneratingInvoice: boolean;
  openInvoicePreview: boolean;
  clientProjecPermitList: ProjectPermitInvoiceType[],
  invoicesLoading: boolean,
  selectedInvoicesToPay: {[id:number]:boolean},
  selectedInvoice: any,
  totalStripeAmount: number,
  selectedInvoicesDataToPay: any[]
  showPartialPaymentPopup: boolean;
  permitFeesToManualyPay: FeeItemType[];
  showDeleteMessage: boolean;
  deleteError: boolean;
}

const initialState: FeesState = {
  inspectionInvoicesList: [{} as PermitFeeItem],
  selectedProjectInvoice: {} as ProjectPermitInvoiceType,
  selectedFee: {} as PermitFeeItem,
  permitFees: [] as FeeItemType[],
  invoicePdf: '',
  invoicePdfInvoiceNumber: '',
  invoiceNumber: '',
  isGeneratingInvoice: false,
  hasViewClicked: false,
  openInvoicePreview: false,
  clientProjecPermitList: [],
  invoicesLoading: false,
  invoiceHeader: {} as InvoiceHeader,
  selectedInvoicesToPay: {},
  selectedInvoice: undefined,
  totalStripeAmount: 0,
  selectedInvoicesDataToPay: [],
  showPartialPaymentPopup: false,
  permitFeesToManualyPay: [] as FeeItemType[],
  showDeleteMessage: false,
  deleteError: false,
  invoiceBody: [
    {
      lineItems: [],
    },
  ],

};

export const feesSlice = createSlice({
  name: 'fees',
  initialState,
  reducers: {
    setPermitInvoicesList: (state, action: PayloadAction<PermitFeeItem[]>) => {
      state.inspectionInvoicesList = action.payload;
    },
    setSelectedProjectInvoice: (state, action: PayloadAction<ProjectPermitInvoiceType>) => {
      state.selectedProjectInvoice = action.payload;
      localStorage.setItem(SELECTED_PERMIT_ID, `${action.payload.permit.id}`);
    },
    setSelectedFee: (state, action: PayloadAction<PermitFeeItem>) => {
      state.selectedFee = action.payload;
    },
    setPermitFees: (state, action: PayloadAction<FeeItemType[]>) => {
      state.permitFees = action.payload;
    },
    setInvoiceHeader: (state, action: PayloadAction<InvoiceHeader>) => {
      state.invoiceHeader = action.payload;
    },
    setInvoiceBody: (state, action: PayloadAction<InvoicePage[]>) => {
      state.invoiceBody = action.payload;
    },
    setInvoicePDF: (state, action: PayloadAction<string>) => {
      state.invoicePdf = action.payload;
      // EA-1391: this legacy setter does not know which invoice the bytes
      // belong to, so it can never leave a PDF "bound" to an invoice. Clearing
      // the tag here means a leftover tag can't falsely enable Send after a
      // reset (setInvoicePDF('')) or an unrelated preview write.
      state.invoicePdfInvoiceNumber = '';
    },
    // EA-1391: set the generated PDF together with the invoice number it was
    // built for, atomically, so Send can verify the attachment matches the
    // invoice in the payload.
    setInvoicePdfForInvoice: (
      state,
      action: PayloadAction<{ pdf: string; invoiceNumber: string }>,
    ) => {
      state.invoicePdf = action.payload.pdf;
      state.invoicePdfInvoiceNumber = action.payload.pdf
        ? action.payload.invoiceNumber
        : '';
    },
    setInvoiceNumber: (state, action: PayloadAction<string>) => {
      state.invoiceNumber = action.payload;
    },
    setHasViewClicked: (state, action: PayloadAction<boolean>) => {
      state.hasViewClicked = action.payload;
    },
    setIsGeneratingInvoice: (state, action: PayloadAction<boolean>) => {
      state.isGeneratingInvoice = action.payload;
    },
    setOpenInvoicePreview: (state, action: PayloadAction<boolean>) => {
      state.openInvoicePreview = action.payload;
    },
    setClientProjecPermitList: (state, action: PayloadAction<ProjectPermitInvoiceType[]>) => {
      state.clientProjecPermitList = action.payload;
      state.invoicesLoading = false;
    },
    setInvoicesLoading: (state, action: PayloadAction<boolean>) => {
      state.invoicesLoading = action.payload;
    },
    setSelectedInvoicesToPay: (state, action: PayloadAction<{[id:number]:boolean}>) => {
      state.selectedInvoicesToPay = action.payload;
    },
    setSelectedInvoice: (state, action: PayloadAction<any>) => {
      state.selectedInvoice = action.payload;
    },
    setTotalStripeAmount: (state, action: PayloadAction<number>) => {
      state.totalStripeAmount = action.payload;
    },
    setSelectedInvoicesDataToPay: (state, action: PayloadAction<any[]>) => {
      state.selectedInvoicesDataToPay = action.payload;
      localStorage.setItem(INVOICES_TO_PAY_KEY, JSON.stringify(action.payload));
    },
    setShowPartialPaymentPopup: (state, action: PayloadAction<boolean>) => {
      state.showPartialPaymentPopup = action.payload;
    },
    setPermitFeesToManualyPay: (state, action: PayloadAction<FeeItemType[]>) => {
      state.permitFeesToManualyPay = action.payload;
    },
    setShowDeleteMessage: (state, action: PayloadAction<boolean>) => {
      state.showDeleteMessage = action.payload;
    },
    setDeleteError: (state, action: PayloadAction<boolean>) => {
      state.deleteError = action.payload;
    },
    resetFees: state => {
      state.inspectionInvoicesList = [{} as PermitFeeItem];
      state.selectedFee = {} as PermitFeeItem;
      state.showPartialPaymentPopup = false;
      state.permitFeesToManualyPay = [];
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setShowDeleteMessage,
  setDeleteError,
  setPermitInvoicesList,
  setSelectedProjectInvoice,
  setSelectedFee,
  resetFees,
  setPermitFees,
  setInvoiceBody,
  setInvoiceHeader,
  setInvoicePDF,
  setInvoicePdfForInvoice,
  setInvoiceNumber,
  setIsGeneratingInvoice,
  setHasViewClicked,
  setOpenInvoicePreview,
  setClientProjecPermitList,
  setInvoicesLoading,
  setSelectedInvoicesToPay,
  setTotalStripeAmount,
  setSelectedInvoicesDataToPay,
  setSelectedInvoice,
  setShowPartialPaymentPopup,
  setPermitFeesToManualyPay,
} = feesSlice.actions;

export default feesSlice.reducer;
